import type { Room, TeamSlot } from '../../network/room';
import { ALLIANCE_LETTERS, ALLIANCE_COLORS } from '../../network/room';
import type { GameSettings } from '../../core/gameSettings';
import { DEFAULT_AMMO } from '../../logic/team';
import { MATCH_ITEMS } from '../../logic/matchItems';
import { TANK_TYPES, TANK_TYPE_IDS, DEFAULT_TANK_TYPE } from '../../core/tankTypes';

export interface HostLobbyData {
  roomCode: string;
  teamsHtml: string;
  playersHtml: string;
  settingsHtml: string;
  canStart: boolean;
  startHint: string;
  connHtml: string;
  qrCodeDataUrl: string;
  playerCount: number;
  maxPlayers: number;
}

export interface ClientLobbyData {
  roomName: string;
  teamsHtml: string;
  playersHtml: string;
  connHtml: string;
  playerCount: number;
  maxPlayers: number;
}

export interface LocalLobbyData {
  roomName: string;
  teamsHtml: string;
  playersHtml: string;
  settingsHtml: string;
  canStart: boolean;
  startHint: string;
  playerCount: number;
  maxPlayers: number;
}

/**
 * LobbyRenderer - Pure rendering functions for lobby screens
 * Extracted from lobbyScreen.ts to reduce file size and improve testability
 */
export class LobbyRenderer {
  static renderMainMenu(savedName: string, gitHash: string, buildTime: string): string {
    return `
      <div class="lobby-container lobby-main vv-bg">
        <div class="title-wrap">
          <div class="title-mascot">${LobbyRenderer.wormMascotSvg()}</div>
          <h1 class="lobby-title vv-title">VVORMS</h1>
          <div class="title-tagline">Turn-based artillery mayhem</div>
        </div>
        <div class="lobby-menu">
          <button id="btn-local-game" class="vv-btn vv-btn--gold main-btn">
            <span class="btn-ico">▶</span> Local Game
          </button>
          <button id="btn-host-game" class="vv-btn vv-btn--secondary main-btn">
            <span class="btn-ico">📡</span> Host Online
          </button>
          <button id="btn-join-game" class="vv-btn vv-btn--secondary main-btn">
            <span class="btn-ico">🔗</span> Join Online
          </button>
          <button id="btn-settings" class="vv-btn vv-btn--ghost main-btn">
            <span class="btn-ico">⚙</span> Settings
          </button>
        </div>
        <div class="lobby-input-group main-name">
          <label for="player-name">Your Name</label>
          <input type="text" id="player-name" class="vv-input" value="${savedName}" maxlength="20" />
        </div>
        <div class="lobby-version">${gitHash} · ${buildTime}</div>
      </div>
    `;
  }

  /** Inline cartoon worm mascot (pink, big-eyed) — procedural, no art asset. */
  static wormMascotSvg(): string {
    return `
      <svg viewBox="0 0 120 120" width="100%" height="100%" aria-hidden="true">
        <ellipse cx="60" cy="112" rx="34" ry="7" fill="rgba(0,0,0,0.28)"/>
        <path d="M40 110 Q30 70 46 54 Q60 40 56 22" fill="none" stroke="#d94f8f" stroke-width="20" stroke-linecap="round"/>
        <path d="M40 110 Q30 70 46 54 Q60 40 56 22" fill="none" stroke="#f06fad" stroke-width="13" stroke-linecap="round"/>
        <circle cx="58" cy="22" r="18" fill="#f06fad" stroke="#b83c75" stroke-width="3"/>
        <circle cx="52" cy="20" r="6" fill="#fff"/>
        <circle cx="66" cy="20" r="6" fill="#fff"/>
        <circle cx="53" cy="21" r="3" fill="#1a1a2e"/>
        <circle cx="67" cy="21" r="3" fill="#1a1a2e"/>
        <path d="M54 30 Q58 33 64 30" fill="none" stroke="#b83c75" stroke-width="2.5" stroke-linecap="round"/>
      </svg>
    `;
  }

  static renderLoadingScreen(message: string): string {
    return `
      <div class="lobby-container lobby-main vv-bg">
        <h1 class="lobby-title vv-title">VVORMS</h1>
        <div class="loading-message">${message}</div>
      </div>
    `;
  }

  static renderHostLobby(data: HostLobbyData): string {
    return `
      <div class="lobby-container vv-bg lobby-room">
        <div class="lobby-room-header">
          <span class="lobby-room-badge">HOST</span>
          <h1 class="lobby-room-title vv-title">Game Lobby</h1>
        </div>
        <div class="room-code-section vv-panel">
          <div class="room-share-info">
            <div class="room-code-label">Room Code:</div>
            <div class="room-code">${data.roomCode}</div>
            <button id="btn-copy-code" class="lobby-btn small">Copy Link</button>
          </div>
          <div class="room-qr-code">
            <img src="${data.qrCodeDataUrl}" alt="QR Code" width="120" height="120" />
          </div>
          ${data.connHtml}
        </div>
        
        <div class="lobby-sections">
          <div class="lobby-section">
            <h2>Players (${data.playerCount}/${data.maxPlayers})</h2>
            <div class="players-list">
              ${data.playersHtml}
            </div>
            
            <h2 style="margin-top: 20px;">Game Settings</h2>
            <div class="settings-section">
              ${data.settingsHtml}
            </div>
          </div>
          
          <div class="lobby-section">
            <h2>Teams</h2>
            <div class="teams-list">
              ${data.teamsHtml}
            </div>
          </div>
        </div>
        
        <div class="lobby-actions">
          <button id="btn-back" class="lobby-btn secondary">Leave</button>
          <button id="btn-start" class="lobby-btn primary" ${data.canStart ? '' : 'disabled'}>
            Start Game
          </button>
        </div>
        
         <div class="lobby-hint">
           ${data.startHint}
         </div>
      </div>
    `;
  }

  static renderClientLobby(data: ClientLobbyData): string {
    return `
      <div class="lobby-container vv-bg lobby-room">
        <div class="lobby-room-header">
          <h1 class="lobby-room-title vv-title">${data.roomName}</h1>
        </div>
        ${data.connHtml}
        
        <div class="lobby-sections">
          <div class="lobby-section">
            <h2>Players (${data.playerCount}/${data.maxPlayers})</h2>
            <div class="players-list">
              ${data.playersHtml}
            </div>
          </div>
          
          <div class="lobby-section">
            <h2>Teams</h2>
            <div class="teams-list">
              ${data.teamsHtml}
            </div>
          </div>
        </div>
        
        <div class="lobby-actions">
          <button id="btn-back" class="lobby-btn secondary">Leave</button>
        </div>
        
        <div class="lobby-hint">
          Waiting for host to start the game...
        </div>
      </div>
    `;
  }

  static renderLocalLobby(data: LocalLobbyData): string {
    return `
      <div class="lobby-container vv-bg lobby-room">
        <div class="lobby-room-header">
          <span class="lobby-room-badge">LOCAL</span>
          <h1 class="lobby-room-title vv-title">${data.roomName}</h1>
        </div>
        
        <div class="lobby-sections">
          <div class="lobby-section">
            <h2>Players (${data.playerCount}/${data.maxPlayers})</h2>
            <div class="players-list">
              ${data.playersHtml}
            </div>
            
            <h2 style="margin-top: 20px;">Game Settings</h2>
            <div class="settings-section">
              ${data.settingsHtml}
            </div>
          </div>
          
          <div class="lobby-section">
            <h2>Teams</h2>
            <div class="teams-list">
              ${data.teamsHtml}
            </div>
          </div>
        </div>
        
        <div class="lobby-actions">
          <button id="btn-back" class="lobby-btn secondary">Back</button>
          <button id="btn-start" class="lobby-btn primary" ${data.canStart ? '' : 'disabled'}>
            Start Game
          </button>
        </div>
        
         <div class="lobby-hint">
           ${data.startHint}
         </div>
       </div>
     `;
  }

  static renderGameSettings(
    settings: GameSettings,
    gameSeed: number,
    isHost: boolean,
    maxTeamsPerPlayer?: number,
  ): string {
    const { hazards, worm, terrain, suddenDeath, mode } = settings;

    return `
      <div class="settings-group">
        <div class="setting-select">
          <label for="setting-game-mode">Game Mode:</label>
          <select id="setting-game-mode">
            <option value="classic" ${mode.id === 'classic' ? 'selected' : ''}>Classic (Worms)</option>
            <option value="gunbound" ${mode.id === 'gunbound' ? 'selected' : ''}>GunBound</option>
          </select>
        </div>
        <div class="setting-seed">
          <label for="setting-seed">Game Seed:</label>
          <input type="text" id="setting-seed" value="${gameSeed}" />
          <button id="btn-random-seed" class="seed-btn">🎲</button>
        </div>
        <label class="setting-toggle">
          <input type="checkbox" id="setting-barrels" ${hazards.oilBarrels ? 'checked' : ''} />
          <span>Oil Barrels</span>
        </label>
        <label class="setting-toggle">
          <input type="checkbox" id="setting-mines" ${hazards.prePlacedMines ? 'checked' : ''} />
          <span>Pre-placed Mines</span>
        </label>
        <label class="setting-toggle">
          <input type="checkbox" id="setting-crates" ${worm.crateDropChance > 0 ? 'checked' : ''} />
          <span>Crate Drops</span>
        </label>
        <label class="setting-toggle">
          <input type="checkbox" id="setting-girders" ${terrain.girders ? 'checked' : ''} />
          <span>Girders</span>
        </label>
        <label class="setting-toggle">
          <input type="checkbox" id="setting-graves" ${worm.gravesOnDeath ? 'checked' : ''} />
          <span>Graves on Death</span>
        </label>
        <label class="setting-toggle">
          <input type="checkbox" id="setting-infinite-ammo" ${worm.infiniteAmmo ? 'checked' : ''} />
          <span>Infinite Ammo</span>
        </label>
        <label class="setting-toggle">
          <input type="checkbox" id="setting-random-tank" ${mode.randomTank ? 'checked' : ''} />
          <span>Random Tank (each team gets a random mobile)</span>
        </label>
        <div class="setting-select">
          <label for="setting-worms-per-team">Worms per Team:</label>
          <select id="setting-worms-per-team">
            ${[1, 2, 3, 4].map((n) => `<option value="${n}" ${(worm.wormsPerTeam ?? 4) === n ? 'selected' : ''}>${n}</option>`).join('')}
          </select>
        </div>
        <div class="setting-number">
          <label for="setting-turn-time">Turn Time (s):</label>
          <input type="number" id="setting-turn-time" value="${settings.turn.turnTime}" min="5" max="180" />
        </div>
        <div class="setting-number">
          <label for="setting-worm-health">Worm Health:</label>
          <input type="number" id="setting-worm-health" value="${worm.initialHealth}" min="1" max="1000" />
        </div>
        <div class="setting-number">
          <label for="setting-movement-budget">Movement/Turn (px, 0 = unlimited):</label>
          <input type="number" id="setting-movement-budget" value="${mode.worm.movementBudgetPx ?? 0}" min="0" max="2000" />
        </div>
        ${
          mode.items.whitelist
            ? `
        <details class="weapon-whitelist">
          <summary>Allowed Weapons (${mode.items.whitelist.length})</summary>
          <div class="whitelist-grid">
            ${Object.keys(DEFAULT_AMMO)
              .map(
                (id) => `
              <label class="setting-toggle">
                <input type="checkbox" class="whitelist-toggle" data-weapon-id="${id}" ${mode.items.whitelist!.includes(id) ? 'checked' : ''} />
                <span>${id}</span>
              </label>`,
              )
              .join('')}
          </div>
        </details>`
            : ''
        }
        ${
          mode.weather.enabled
            ? `
        <div class="setting-number">
          <label for="setting-weather-interval">Weather change every (turns):</label>
          <input type="number" id="setting-weather-interval" value="${mode.weather.intervalTurns}" min="1" max="20" />
        </div>`
            : ''
        }
        <details class="match-items">
          <summary>Match Items (${Object.values(mode.items.matchItems).reduce((sum, n) => sum + n, 0)} per team)</summary>
          <div class="whitelist-grid">
            ${Object.values(MATCH_ITEMS)
              .map(
                (item) => `
              <div class="setting-number" title="${item.description}">
                <label>${item.name}:</label>
                <input type="number" class="match-item-count" data-item-id="${item.id}" value="${mode.items.matchItems[item.id] ?? 0}" min="0" max="9" />
              </div>`,
              )
              .join('')}
          </div>
        </details>
        <div class="setting-select">
          <label for="setting-map-style">Map Style:</label>
          <select id="setting-map-style">
            <optgroup label="Generated">
              <option value="random" ${terrain.style === 'random' ? 'selected' : ''}>Random Hills</option>
              <option value="island" ${terrain.style === 'island' ? 'selected' : ''}>Islands</option>
              <option value="bridges" ${terrain.style === 'bridges' ? 'selected' : ''}>Bridges</option>
              <option value="towers" ${terrain.style === 'towers' ? 'selected' : ''}>Towers</option>
              <option value="fortress" ${terrain.style === 'fortress' ? 'selected' : ''}>Fortress</option>
              <option value="tunnels" ${terrain.style === 'tunnels' ? 'selected' : ''}>Tunnels</option>
              <option value="platforms" ${terrain.style === 'platforms' ? 'selected' : ''}>Platforms</option>
            </optgroup>
            <optgroup label="Classic">
              <option value="open" ${terrain.style === 'open' ? 'selected' : ''}>Classic Open</option>
              <option value="cavern" ${terrain.style === 'cavern' ? 'selected' : ''}>Cavern</option>
            </optgroup>
          </select>
        </div>
        <div class="setting-select" id="theme-container" style="${['open', 'cavern'].includes(terrain.style) ? 'display: none;' : ''}">
          <label for="setting-theme">Theme:</label>
          <select id="setting-theme">
            <option value="random" ${terrain.theme === 'random' ? 'selected' : ''}>Random</option>
            <option value="dirt" ${terrain.theme === 'dirt' ? 'selected' : ''}>Dirt</option>
            <option value="snow" ${terrain.theme === 'snow' ? 'selected' : ''}>Snow</option>
            <option value="jungle" ${terrain.theme === 'jungle' ? 'selected' : ''}>Jungle</option>
            <option value="desert" ${terrain.theme === 'desert' ? 'selected' : ''}>Desert</option>
            <option value="hell" ${terrain.theme === 'hell' ? 'selected' : ''}>Hell</option>
            <option value="alien" ${terrain.theme === 'alien' ? 'selected' : ''}>Alien</option>
          </select>
        </div>
        <div class="setting-select" id="complexity-container" style="${['open', 'cavern'].includes(terrain.style) ? 'display: none;' : ''}">
          <label for="setting-complexity">Complexity:</label>
          <select id="setting-complexity">
            <option value="simple" ${terrain.complexity === 'simple' ? 'selected' : ''}>Simple</option>
            <option value="normal" ${terrain.complexity === 'normal' ? 'selected' : ''}>Normal</option>
            <option value="complex" ${terrain.complexity === 'complex' ? 'selected' : ''}>Complex</option>
          </select>
        </div>
        <label class="setting-toggle" id="floating-islands-container" style="${terrain.style === 'random' ? '' : 'display: none;'}">
          <input type="checkbox" id="setting-floating-islands" ${terrain.floatingIslands ? 'checked' : ''} />
          <span>Floating Islands</span>
        </label>
        <label class="setting-toggle" id="bridges-container" style="${terrain.style === 'random' ? '' : 'display: none;'}">
          <input type="checkbox" id="setting-bridges" ${terrain.bridges ? 'checked' : ''} />
          <span>Natural Bridges</span>
        </label>
        <label class="setting-toggle">
          <input type="checkbox" id="setting-sudden-death" ${suddenDeath.enabled ? 'checked' : ''} />
          <span>Sudden Death</span>
        </label>
        <div class="setting-number" id="sudden-death-turns-container" style="${suddenDeath.enabled ? '' : 'display: none;'}">
          <label for="setting-sudden-death-turns">Turns before Sudden Death:</label>
          <input type="number" id="setting-sudden-death-turns" value="${suddenDeath.turnsBeforeStart}" min="5" max="100" />
        </div>
        ${
          isHost && maxTeamsPerPlayer !== undefined
            ? `
        <div class="setting-number">
          <label for="max-teams-per-player">Max Teams Per Player:</label>
          <input type="number" id="max-teams-per-player" min="1" max="8" value="${maxTeamsPerPlayer}" />
        </div>
        `
            : ''
        }
      </div>
    `;
  }

  static renderTeamSlots(
    teamSlots: TeamSlot[],
    localPlayerId: string,
    room: Room,
    randomizeOnStart = false,
    tankSelection = false,
  ): string {
    const localPlayer = room.getPlayer(localPlayerId);
    const localPlayerTeamCount = localPlayer ? localPlayer.teamIds.length : 0;
    const hasReachedMaxTeams = localPlayerTeamCount >= room.maxTeamsPerPlayer;
    const isLocalHost = room.isHost(localPlayerId);

    const letters = ALLIANCE_LETTERS.slice(0, room.allianceCount);
    const inRange = (a: string | undefined): boolean =>
      !!a && (letters as readonly string[]).includes(a);

    const toolbar = isLocalHost
      ? `
      <div class="alliance-toolbar">
        <label class="alliance-count-label">Alliances
          <select class="alliance-count-select">
            ${[2, 3, 4].map((n) => `<option value="${n}" ${room.allianceCount === n ? 'selected' : ''}>${n}</option>`).join('')}
          </select>
        </label>
        <label class="randomize-on-start-label" title="Shuffle teams into balanced alliances right before the match starts">
          <input type="checkbox" class="randomize-on-start-check" ${randomizeOnStart ? 'checked' : ''} />
          \u{1f3b2} Shuffle alliances on start
        </label>
      </div>`
      : '';

    const columns = letters
      .map((letter) => {
        const accent = '#' + (ALLIANCE_COLORS[letter] ?? 0x888888).toString(16).padStart(6, '0');
        const slots = teamSlots.filter((s) => {
          const a = s.config.allianceId;
          return inRange(a) ? a === letter : letter === letters[0];
        });
        const claimed = slots.filter((s) => s.ownerId !== null).length;
        const cards = slots
          .map((slot) =>
            LobbyRenderer.renderTeamSlotCard(
              slot,
              localPlayerId,
              room,
              isLocalHost,
              hasReachedMaxTeams,
              tankSelection,
            ),
          )
          .join('');
        return `
        <div class="alliance-column" data-alliance="${letter}" style="--alliance-accent: ${accent}">
          <div class="alliance-header">Alliance ${letter}<span class="alliance-tag">${claimed} team${claimed === 1 ? '' : 's'}</span></div>
          ${cards || '<div class="alliance-empty">No teams here</div>'}
        </div>`;
      })
      .join('');

    return `${toolbar}<div class="alliance-columns" style="--alliance-cols: ${letters.length}">${columns}</div>`;
  }

  private static renderTeamSlotCard(
    slot: TeamSlot,
    localPlayerId: string,
    room: Room,
    isLocalHost: boolean,
    hasReachedMaxTeams: boolean,
    tankSelection: boolean,
  ): string {
    const owner = slot.ownerId ? room.getPlayer(slot.ownerId) : null;
    const isOwned = slot.ownerId !== null;
    const isOwnedByLocal = slot.ownerId === localPlayerId;
    const canSetAlliance = isOwned && (isOwnedByLocal || isLocalHost);
    const canSetTank = isOwnedByLocal || isLocalHost;
    const currentTank = slot.config.tankType ?? DEFAULT_TANK_TYPE;
    const colorHex = '#' + slot.config.color.toString(16).padStart(6, '0');
    const tankSelectHtml = tankSelection
      ? `<select class="tank-type-select" data-team-id="${slot.config.id}"
                title="Tank type" ${canSetTank ? '' : 'disabled'}>
          ${TANK_TYPE_IDS.map(
            (id) =>
              `<option value="${id}" ${id === currentTank ? 'selected' : ''}>${TANK_TYPES[id].name}</option>`,
          ).join('')}
        </select>`
      : '';

    return `
        <div class="team-slot ${isOwned ? 'claimed' : 'available'}" data-team-id="${slot.config.id}">
          <div class="team-color" style="background-color: ${colorHex}"></div>
          <div class="team-info">
            <input type="text" 
                   class="team-name-input" 
                   data-team-id="${slot.config.id}"
                   value="${slot.config.name}" 
                   maxlength="20" 
                   ${isOwnedByLocal ? '' : 'readonly'} />
            <div class="team-worms">${slot.config.wormNames.join(', ')}</div>
          </div>
          <div class="team-slot-actions">
            ${tankSelectHtml}
            <button class="alliance-btn" data-team-id="${slot.config.id}"
                    title="Alliance group: click to move this team to the next alliance"
                    ${canSetAlliance ? '' : 'disabled'}>
              ${slot.config.allianceId ?? '-'}
            </button>
            <div class="team-owner">
              ${isOwned ? owner?.name || 'Unknown' : 'Available'}
            </div>
            <button class="team-btn ${isOwnedByLocal ? 'release' : 'claim'}" 
                    ${isOwned && !isOwnedByLocal ? 'disabled' : ''}
                    ${!isOwnedByLocal && hasReachedMaxTeams ? 'disabled' : ''}>
              ${isOwnedByLocal ? 'Release' : 'Claim'}
            </button>
          </div>
        </div>
      `;
  }

  static renderPlayerList(room: Room, localPlayerId: string): string {
    const isHost = room.isHost(localPlayerId);

    return room
      .getAllPlayers()
      .map((player) => {
        const isSpectator = player.isSpectator;
        const hasTeam = player.teamIds.length === 1;
        const statusIcon = isSpectator ? '👁️' : hasTeam ? '✅' : '⚠️';
        const statusClass = isSpectator ? 'spectator' : hasTeam ? 'ready' : 'not-ready';
        const canKick = isHost && player.id !== localPlayerId;
        const isLocalPlayer = player.id === localPlayerId;

        return `
        <div class="player-item ${isLocalPlayer ? 'local' : ''} ${statusClass}">
          <span class="player-status">${statusIcon}</span>
          <span class="player-name">${player.name}</span>
          ${room.isHost(player.id) ? '<span class="host-badge">HOST</span>' : ''}
          ${
            isSpectator
              ? '<span class="spectator-badge">SPECTATOR</span>'
              : `<span class="player-teams">(${player.teamIds.length}/${room.maxTeamsPerPlayer} teams)</span>`
          }
          ${
            isLocalPlayer
              ? `
            <label class="spectator-toggle-small">
              <input type="checkbox" class="spectator-checkbox" ${isSpectator ? 'checked' : ''} />
              <span>Spectate</span>
            </label>
          `
              : ''
          }
          ${canKick ? `<button class="kick-btn" data-player-id="${player.id}">Kick</button>` : ''}
        </div>
      `;
      })
      .join('');
  }

  static renderConnectionBadge(
    connInfo: { type: string; isIPv6: boolean; isRelay: boolean },
    peerCount?: number,
  ): string {
    let badgeClass = 'conn-ipv4';
    let icon = '🌐';
    if (connInfo.isRelay) {
      badgeClass = 'conn-relay';
      icon = '🔄';
    } else if (connInfo.isIPv6) {
      badgeClass = 'conn-ipv6';
      icon = '⚡';
    }
    const peersLabel =
      peerCount !== undefined ? ` (${peerCount} peer${peerCount !== 1 ? 's' : ''})` : '';
    return `
      <div class="connection-badge ${badgeClass}">
        ${icon} ${connInfo.type}${peersLabel}
      </div>
    `;
  }

  static getStartHint(room: Room, canStart: boolean): string {
    if (canStart) {
      return 'Ready to start!';
    }

    const claimedTeams = room.getTeamSlots().filter((s) => s.ownerId !== null).length;
    const activePlayers = room.getAllPlayers().filter((p) => !p.isSpectator);
    const playersWithoutTeams = activePlayers.filter((p) => p.teamIds.length === 0);
    const spectatorCount = room.getAllPlayers().filter((p) => p.isSpectator).length;

    if (activePlayers.length === 0) {
      return 'Need at least 1 player (not spectating)';
    }

    if (claimedTeams < 2) {
      return `Need at least 2 teams claimed (currently ${claimedTeams})`;
    }

    if (playersWithoutTeams.length > 0) {
      const names = playersWithoutTeams.map((p) => p.name).join(', ');
      return `${names} must claim at least 1 team`;
    }

    const spectatorText =
      spectatorCount > 0 ? ` (${spectatorCount} spectator${spectatorCount > 1 ? 's' : ''})` : '';
    return `Select teams to start${spectatorText}`;
  }
}
